import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, MapPin, Camera, FileText, Clapperboard, Receipt, SlidersHorizontal } from "lucide-react";
import { BrowserFrame, PhoneFrame, MockShell } from "./Frames";
import { EmailSetupButton, MailLine } from "./EmailCta";
import { OverviewBody } from "./screens/Overview";
import { Clock } from "./screens/PhoneScreens";
import { IMG, goTo } from "./homeData";
import { SEO } from "@/seo/useSeo";

const HOME = SEO.pages["/"];

const STATS = [["TIME-STAMPED", "every check-in, clock-on & note"], ["PHOTO PROOF", "before, after & issue shots"], ["1 TAP", "branded job report to your client"], ["NO APP STORE", "installs from the browser"]];

const Chip = ({ icon: Icon, children, className, delay }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: [0, -6, 0] }}
    transition={{ opacity: { delay }, y: { delay, duration: 5, repeat: Infinity, ease: "easeInOut" } }}
    className={`absolute hidden md:flex items-center gap-2 rounded-xl bg-white/95 text-[#0F172A] px-3 py-2 text-xs font-semibold shadow-2xl ${className}`}>
    <span className="w-7 h-7 rounded-lg bg-[#EA580C]/10 text-[#EA580C] grid place-items-center"><Icon className="w-4 h-4" /></span>{children}
  </motion.div>
);

export const Hero = ({ onStart }) => (
  <section className="relative bg-[#0B1120] text-white overflow-hidden home-noise" data-testid="home-hero">
    <div className="absolute inset-0 home-grid" />
    <div className="absolute inset-y-0 right-0 w-full lg:w-[62%] bg-cover bg-center opacity-50" style={{ backgroundImage: `url(${IMG.hero})` }} />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0B1120] via-[#0B1120]/85 to-[#0B1120]/25" />
    <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-24 lg:pb-32 grid lg:grid-cols-[1fr_1.1fr] gap-14 lg:gap-12 items-center">
      <div>
        <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onClick={() => goTo("invoices")} data-testid="home-hero-new-invoices"
          className="group mb-4 inline-flex items-center gap-2 rounded-full bg-[#EA580C]/15 border border-[#EA580C]/40 pl-1 pr-3 py-1 text-xs sm:text-sm font-semibold text-white hover:bg-[#EA580C]/25 transition-colors">
          <span className="rounded-full bg-[#EA580C] px-2 py-0.5 text-[10px] sm:text-xs font-bold uppercase tracking-wide">New</span>
          Client invoices · pay by card or bank <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
        </motion.button>
        <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="font-display" data-testid="home-hero-title">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 font-body text-xs sm:text-sm font-semibold tracking-normal text-slate-200" data-testid="home-hero-kicker">
            <span className="w-2 h-2 rounded-full bg-[#EA580C] shrink-0" /> {HOME.eyebrow}
          </span>
          <span className="block mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Every job.<br />Every hour.<br /><span className="text-[#EA580C]">Proven.</span>
          </span>
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
          className="mt-5 inline-flex items-center gap-2.5 text-lg md:text-xl font-semibold text-white" data-testid="home-hero-customisable">
          <SlidersHorizontal className="w-5 h-5 text-[#EA580C] shrink-0" /> Fully customisable to suit your business needs.
        </motion.p>
        <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }} className="mt-4 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl" data-testid="home-hero-sub">
          {HOME.intro}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }} className="mt-9 flex flex-wrap gap-3">
          <EmailSetupButton testid="home-hero-email-btn" />
          <button onClick={onStart} data-testid="home-hero-start-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 font-semibold text-white hover:bg-white hover:text-[#0B1120] transition-colors">
            Start free trial
          </button>
        </motion.div>
        <div className="mt-4"><MailLine testid="home-hero-mail" prefix="Get in touch today:" /></div>
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-400">
          <button onClick={() => goTo("proof")} data-testid="home-hero-proof-btn" className="inline-flex items-center gap-1.5 font-semibold text-white underline-offset-4 hover:underline">
            <FileText className="w-4 h-4 text-[#EA580C]" /> See the proof
          </button>
          <button onClick={() => goTo("video")} data-testid="home-hero-video-btn" className="inline-flex items-center gap-1.5 font-semibold text-white underline-offset-4 hover:underline">
            <Clapperboard className="w-4 h-4 text-[#EA580C]" /> Watch the video
          </button>
          <button onClick={() => goTo("walkthroughs")} data-testid="home-hero-tutorials-btn" className="inline-flex items-center gap-1.5 font-semibold text-white underline-offset-4 hover:underline">
            <PlayCircle className="w-4 h-4" /> Watch the walkthroughs
          </button>
          <span className="whitespace-nowrap">Already on Cross Jobs? <Link to="/login" data-testid="home-hero-signin-link" className="font-semibold text-white underline-offset-4 hover:underline">Sign in</Link></span>
        </div>
      </div>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }} className="relative lg:pl-6">
        <BrowserFrame testid="home-hero-browser"><MockShell tab="Overview"><OverviewBody /></MockShell></BrowserFrame>
        <div className="hidden sm:block absolute -bottom-16 -right-3 lg:-right-8 w-[150px] lg:w-[165px]"><PhoneFrame><Clock /></PhoneFrame></div>
        <Chip icon={MapPin} delay={0.9} className="-top-5 left-10">Noah checked in on site · 8:06am</Chip>
        <Chip icon={Camera} delay={1.3} className="-bottom-7 left-2">3 site photos · report sent to client</Chip>
        <Chip icon={Receipt} delay={1.7} className="-top-5 right-4">INV-0046 paid by card · $2,860</Chip>
      </motion.div>
    </div>
    <div className="relative z-10 border-t border-white/10 bg-[#0B1120]/80">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4">
        {STATS.map(([k, v], i) => (
          <div key={k} className={`py-6 lg:py-7 ${i % 2 ? "pl-5" : ""} ${i > 0 ? "lg:pl-8 lg:border-l border-white/10" : ""}`} data-testid={`home-stat-${i}`}>
            <p className="font-mono text-sm sm:text-base font-bold text-[#EA580C]">{k}</p>
            <p className="text-xs sm:text-sm text-slate-400 mt-1">{v}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);
